// Badging API wrapper - paints the unread-notification count on the installed
// app icon.
//
// Platform note: Android/Chrome badges an installed app with no extra
// permission, but iOS (16.4+) only paints the badge once the user has granted
// notification permission to the installed web app. Until then every
// setAppBadge call is rejected, which is why `requestBadgePermission` exists
// and why the last count is remembered - so the badge can be painted the
// moment permission arrives.

let lastCount = 0;

export function isBadgeSupported() {
  return typeof navigator !== 'undefined' && 'setAppBadge' in navigator;
}

export function isStandaloneDisplay() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: fullscreen)').matches ||
    window.matchMedia('(display-mode: minimal-ui)').matches ||
    window.navigator.standalone === true
  );
}

/** True when the badge would work here but the user has not been asked yet. */
export function needsBadgePermission() {
  if (!isBadgeSupported() || typeof Notification === 'undefined') return false;
  return Notification.permission === 'default';
}

export function setAppBadge(count) {
  const value = Number(count);
  lastCount = Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;
  applyBadge();
}

/** Re-paints whatever count was last handed to setAppBadge. */
export function applyBadge() {
  if (!isBadgeSupported()) return;

  if (lastCount <= 0) {
    clearAppBadge();
    return;
  }

  Promise.resolve(navigator.setAppBadge(lastCount)).catch((error) => {
    // iOS rejects until notification permission is granted; nothing to do here
    // beyond leaving the count remembered for the next applyBadge().
    if (process.env.NODE_ENV !== 'production') {
      console.warn('setAppBadge rejected:', error?.message || error);
    }
  });
}

export function clearAppBadge() {
  lastCount = 0;
  if (typeof navigator === 'undefined' || !('clearAppBadge' in navigator)) return;
  Promise.resolve(navigator.clearAppBadge()).catch(() => undefined);
}

/**
 * Asks for the notification permission iOS requires before it will badge the
 * icon, then paints the pending count. Must be called from a user gesture -
 * Safari rejects the request otherwise.
 *
 * Resolves to the resulting permission string, or 'unsupported'.
 */
export async function requestBadgePermission() {
  if (!isBadgeSupported() || typeof Notification === 'undefined') return 'unsupported';
  if (Notification.permission !== 'default') return Notification.permission;

  try {
    // Older Safari only supports the callback form and returns undefined.
    const result = await new Promise((resolve) => {
      const maybePromise = Notification.requestPermission(resolve);
      if (maybePromise && typeof maybePromise.then === 'function') {
        maybePromise.then(resolve).catch(() => resolve(Notification.permission));
      }
    });

    const permission = result || Notification.permission;
    if (permission === 'granted') applyBadge();
    return permission;
  } catch (error) {
    return Notification.permission;
  }
}
